"use client";

import { useState } from "react";
import { FileQuestion } from "lucide-react";
import { Button } from "./ui/button";
import { DocumentRequestModal } from "./DocumentRequestModal";

interface DocumentRequestButtonProps {
  carrierName: string;
  carrierId?: string;
  reason?: string;
  className?: string;
}

/**
 * DocumentRequestButton - Shows a button to request missing carrier documents
 * Can be embedded in AI chat responses when a carrier doesn't have documents
 */
export function DocumentRequestButton({
  carrierName,
  carrierId,
  reason,
  className = "",
}: DocumentRequestButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prefilledData] = useState({
    carrierName,
    carrierId,
    documentType: "underwriting-guide",
    requestReason: reason || `Need underwriting documentation for ${carrierName}`,
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`gap-2 ${className}`}
        onClick={() => setIsModalOpen(true)}
      >
        <FileQuestion className="h-4 w-4" />
        Request {carrierName} Documents
      </Button>

      <DocumentRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          // Could add a toast notification here if desired
        }}
        prefilledCarrier={prefilledData.carrierId}
        prefilledDocumentType={prefilledData.documentType}
        prefilledReason={prefilledData.requestReason}
      />
    </>
  );
}

/**
 * parseAIResponseForMissingCarriers - Detects carriers without documents in AI response
 * Returns array of carrier names that need documents
 */
export function parseAIResponseForMissingCarriers(aiResponse: string): string[] {
  const missingCarriers: string[] = [];

  // Pattern 1: "❌ CarrierName: ✗ No documents"
  const pattern1 = /❌\s*([^:]+):\s*✗\s*No documents?/gi;
  let match1;
  while ((match1 = pattern1.exec(aiResponse)) !== null) {
    missingCarriers.push(match1[1].trim());
  }

  // Pattern 2: "CarrierName: ✗ No documents"
  const pattern2 = /([A-Z][a-zA-Z\s&]+):\s*✗\s*No documents?/g;
  let match2;
  while ((match2 = pattern2.exec(aiResponse)) !== null) {
    const carrier = match2[1].trim();
    if (!missingCarriers.includes(carrier)) {
      missingCarriers.push(carrier);
    }
  }

  // Pattern 3: "I don't have underwriting documentation for CarrierName"
  const pattern3 = /don'?t\s+(?:currently\s+)?have\s+(?:underwriting\s+)?documentation\s+for\s+([A-Z][a-zA-Z\s&]+)/gi;
  let match3;
  while ((match3 = pattern3.exec(aiResponse)) !== null) {
    const carrier = match3[1].trim();
    if (!missingCarriers.includes(carrier)) {
      missingCarriers.push(carrier);
    }
  }

  // Pattern 4: "no documents available for CarrierName"
  const pattern4 = /no\s+documents?\s+available\s+for\s+([A-Z][a-zA-Z\s&]+)/gi;
  let match4;
  while ((match4 = pattern4.exec(aiResponse)) !== null) {
    const carrier = match4[1].trim();
    if (!missingCarriers.includes(carrier)) {
      missingCarriers.push(carrier);
    }
  }

  return missingCarriers;
}

/**
 * enhanceAIResponseWithRequestButtons - Adds request buttons for missing carriers
 * Used by UnifiedInsuraBot to make AI responses interactive
 */
export function enhanceAIResponseWithRequestButtons(
  aiResponse: string,
  availableCarriers: Array<{ id: string; name: string; slug: string }>,
): { enhancedResponse: string; missingCarriers: Array<{ id: string; name: string }> } {
  const detectedMissingCarriers = parseAIResponseForMissingCarriers(aiResponse);

  const missingCarriers = detectedMissingCarriers
    .map((carrierName) => {
      // Try to find carrier ID from available carriers list
      const carrier = availableCarriers.find(
        (c) => c.name.toLowerCase() === carrierName.toLowerCase()
      );
      return carrier
        ? { id: carrier.id, name: carrier.name }
        : { id: "", name: carrierName };
    })
    .filter((c) => c.name); // Remove empty entries

  // The actual button rendering will happen in UnifiedInsuraBot component
  // This function just returns the data
  return {
    enhancedResponse: aiResponse,
    missingCarriers,
  };
}
